import { useState, useEffect, useCallback } from 'react';
import { Table, Badge, Button, Modal, Input, Select, Pagination } from '../../components/ui';
import { apiFetch } from '../../services/api';

const AVAILABILITY_COLORS = {
  available: 'var(--green-light)',
  busy:      'var(--yellow)',
  inactive:  'var(--text-muted)',
};

export default function TrainersPage() {
  const [tab, setTab]             = useState('trainers');
  const [trainers, setTrainers]   = useState([]);
  const [users, setUsers]         = useState([]);
  const [cohorts, setCohorts]     = useState([]);
  const [assignments, setAssign]  = useState([]);
  const [pagination, setPag]      = useState(null);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null);
  const [selected, setSel]        = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState({});
  const [assignForm, setAssignForm] = useState({});
  const [selectedCohort, setSelectedCohort] = useState('');

  const token = localStorage.getItem('mh_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadTrainers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/admin/trainers?limit=50', { headers });
      const data = await res.json();
      setTrainers(data.data || []); setPag(data.pagination);
    } catch { setTrainers([]); } finally { setLoading(false); }
  }, []);

  const loadUsers = async () => {
    const res = await apiFetch('/users?limit=100', { headers });
    const data = await res.json();
    setUsers(data.data || []);
  };

  const loadCohorts = async () => {
    const res = await apiFetch('/programs/cohorts', { headers });
    const data = await res.json();
    setCohorts(data.data || []);
  };

  const loadAssignments = useCallback(async () => {
    if (!selectedCohort) { setAssign([]); return; }
    setLoading(true);
    try {
      const res = await apiFetch(`/admin/cohort-trainers?cohort_id=${selectedCohort}`, { headers });
      const data = await res.json();
      setAssign(data.data || []);
    } catch { setAssign([]); } finally { setLoading(false); }
  }, [selectedCohort]);

  useEffect(() => { loadTrainers(); loadCohorts(); }, [loadTrainers]);
  useEffect(() => { if (tab === 'assignments') loadAssignments(); }, [tab, loadAssignments]);

  const openCreate = async () => {
    await loadUsers();
    setForm({ user_id: '', bio: '', expertise: '', certifications: '', linkedin_url: '', portfolio_url: '', hourly_rate: '', availability: 'available', notes: '' });
    setSel(null); setModal('trainer');
  };

  const openEdit = async (t) => {
    await loadUsers();
    setForm({
      user_id: t.user_id, bio: t.bio || '',
      expertise: (t.expertise || []).join(', '),
      certifications: (t.certifications || []).join(', '),
      linkedin_url: t.linkedin_url || '', portfolio_url: t.portfolio_url || '',
      hourly_rate: t.hourly_rate || '', availability: t.availability || 'available',
      status: t.status || 'active', notes: t.notes || '',
    });
    setSel(t); setModal('trainer');
  };

  const handleSaveTrainer = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        expertise: form.expertise.split(',').map(s => s.trim()).filter(Boolean),
        certifications: form.certifications.split(',').map(s => s.trim()).filter(Boolean),
        hourly_rate: parseFloat(form.hourly_rate) || 0,
      };
      if (!selected) {
        await apiFetch('/admin/trainers', { method: 'POST', headers, body: JSON.stringify(payload) });
      } else {
        await apiFetch(`/admin/trainers/${selected.id}`, { method: 'PUT', headers, body: JSON.stringify(payload) });
      }
      setModal(null); loadTrainers();
    } catch { alert('Save failed'); } finally { setSaving(false); }
  };

  const openAssign = () => {
    setAssignForm({ trainer_id: '', role: 'lead', fee_amount: '', fee_currency: 'RWF', notes: '' });
    setModal('assign');
  };

  const handleAssign = async () => {
    setSaving(true);
    try {
      await apiFetch('/admin/cohort-trainers', {
        method: 'POST', headers,
        body: JSON.stringify({ ...assignForm, cohort_id: selectedCohort, fee_amount: parseFloat(assignForm.fee_amount) || 0 }),
      });
      setModal(null); loadAssignments();
    } catch { alert('Save failed'); } finally { setSaving(false); }
  };

  const handleRemove = async (cohortId, trainerId) => {
    if (!window.confirm('Remove trainer from this cohort?')) return;
    await apiFetch(`/admin/cohort-trainers/${cohortId}/${trainerId}`, { method: 'DELETE', headers });
    loadAssignments();
  };

  const TRAINER_COLS = [
    { key: 'user_name', label: 'Trainer', render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.user_email}</div>
      </div>
    )},
    { key: 'expertise', label: 'Expertise', render: v => (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {(v || []).slice(0, 3).map(e => (
          <span key={e} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'var(--accent-muted)', color: 'var(--accent)' }}>{e}</span>
        ))}
        {(v || []).length > 3 && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>+{v.length - 3}</span>}
      </div>
    )},
    { key: 'hourly_rate', label: 'Rate/hr', render: (v, row) => v ? <span style={{ fontSize: 12 }}>{row.rate_currency} {Number(v).toLocaleString()}</span> : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span> },
    { key: 'total_cohorts', label: 'Cohorts', render: v => <span style={{ fontSize: 12 }}>{v || 0}</span> },
    { key: 'availability', label: 'Status', render: v => <span style={{ fontSize: 11, fontWeight: 600, color: AVAILABILITY_COLORS[v] || 'var(--text-muted)', textTransform: 'capitalize' }}>{v}</span> },
    { key: 'rating', label: 'Rating', render: v => v ? <span style={{ fontSize: 12, color: 'var(--accent)' }}>★ {Number(v).toFixed(1)}</span> : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span> },
    { key: '_actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
        <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>Edit</Button>
      </div>
    )},
  ];

  const ASSIGN_COLS = [
    { key: 'trainer_name', label: 'Trainer', render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.trainer_email}</div>
      </div>
    )},
    { key: 'role', label: 'Role', render: v => <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'capitalize', color: v === 'lead' ? 'var(--accent)' : 'var(--text-secondary)' }}>{v}</span> },
    { key: 'expertise', label: 'Expertise', render: v => (v || []).slice(0,2).join(', ') || '—' },
    { key: 'fee_amount', label: 'Fee', render: (v, row) => v ? `${row.fee_currency} ${Number(v).toLocaleString()}` : '—' },
    { key: 'fee_status', label: 'Fee Status', render: v => <Badge status={v} /> },
    { key: '_actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
        <Button variant="ghost" size="sm" onClick={() => handleRemove(row.cohort_id, row.trainer_id)}>Remove</Button>
      </div>
    )},
  ];

  const tabBtn = (t) => ({
    padding: '9px 18px', fontSize: 13, cursor: 'pointer', background: 'none', border: 'none',
    borderBottom: `2px solid ${tab === t ? 'var(--accent)' : 'transparent'}`,
    color: tab === t ? 'var(--accent)' : 'var(--text-muted)',
    fontWeight: tab === t ? 600 : 400, marginBottom: -1, transition: 'all 0.15s',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)' }}>
        <button style={tabBtn('trainers')}    onClick={() => setTab('trainers')}>Trainers</button>
        <button style={tabBtn('assignments')} onClick={() => setTab('assignments')}>Cohort Assignments</button>
      </div>

      {tab === 'trainers' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{pagination?.total ?? 0} trainers</span>
            <Button onClick={openCreate}>+ Add Trainer</Button>
          </div>
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            <Table columns={TRAINER_COLS} data={trainers} loading={loading} onRowClick={openEdit} />
            <Pagination pagination={pagination} onPage={() => {}} />
          </div>
        </>
      )}

      {tab === 'assignments' && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
            <Select value={selectedCohort} onChange={e => setSelectedCohort(e.target.value)} style={{ width: 300 }}>
              <option value="">— Select a Cohort —</option>
              {cohorts.map(c => <option key={c.id} value={c.id}>{c.program_title} — {c.cohort_name}</option>)}
            </Select>
            {selectedCohort && <Button onClick={openAssign}>+ Assign Trainer</Button>}
          </div>
          {selectedCohort && (
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
              <Table columns={ASSIGN_COLS} data={assignments} loading={loading} />
            </div>
          )}
        </>
      )}

      {/* Trainer modal */}
      <Modal open={modal === 'trainer'} onClose={() => setModal(null)} title={selected ? `Edit — ${selected.user_name}` : 'Add Trainer'} width={620}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ gridColumn: '1/-1' }}>
            <Select label="User Account" value={form.user_id} onChange={e => setForm(f => ({ ...f, user_id: e.target.value }))}>
              <option value="">— Select user —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </Select>
          </div>
          <Select label="Availability" value={form.availability} onChange={e => setForm(f => ({ ...f, availability: e.target.value }))}>
            <option value="available">Available</option>
            <option value="busy">Busy</option>
            <option value="inactive">Inactive</option>
          </Select>
          <Input label="Hourly Rate (RWF)" type="number" value={form.hourly_rate} onChange={e => setForm(f => ({ ...f, hourly_rate: e.target.value }))} />
          <div style={{ gridColumn: '1/-1' }}>
            <Input label="Expertise (comma-separated)" value={form.expertise} onChange={e => setForm(f => ({ ...f, expertise: e.target.value }))} placeholder="React, Node.js, UI Design" />
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <Input label="Certifications (comma-separated)" value={form.certifications} onChange={e => setForm(f => ({ ...f, certifications: e.target.value }))} placeholder="AWS, Google, CISCO" />
          </div>
          <Input label="LinkedIn URL" value={form.linkedin_url} onChange={e => setForm(f => ({ ...f, linkedin_url: e.target.value }))} />
          <Input label="Portfolio URL" value={form.portfolio_url} onChange={e => setForm(f => ({ ...f, portfolio_url: e.target.value }))} />
          <div style={{ gridColumn: '1/-1' }}>
            <label style={labelStyle}>Bio</label>
            <textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} rows={3}
              style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', padding: '9px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
          </div>
          <div style={{ gridColumn: '1/-1', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleSaveTrainer} loading={saving}>{selected ? 'Save Changes' : 'Add Trainer'}</Button>
          </div>
        </div>
      </Modal>

      {/* Assign modal */}
      <Modal open={modal === 'assign'} onClose={() => setModal(null)} title="Assign Trainer to Cohort" width={480}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Select label="Trainer" value={assignForm.trainer_id} onChange={e => setAssignForm(f => ({ ...f, trainer_id: e.target.value }))}>
            <option value="">— Select trainer —</option>
            {trainers.map(t => <option key={t.id} value={t.id}>{t.user_name} — {(t.expertise || []).slice(0,2).join(', ')}</option>)}
          </Select>
          <Select label="Role" value={assignForm.role} onChange={e => setAssignForm(f => ({ ...f, role: e.target.value }))}>
            <option value="lead">Lead Trainer</option>
            <option value="assistant">Assistant</option>
            <option value="guest">Guest Speaker</option>
          </Select>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Input label="Fee Amount" type="number" value={assignForm.fee_amount} onChange={e => setAssignForm(f => ({ ...f, fee_amount: e.target.value }))} />
            <Select label="Currency" value={assignForm.fee_currency} onChange={e => setAssignForm(f => ({ ...f, fee_currency: e.target.value }))}>
              <option value="RWF">RWF</option>
              <option value="USD">USD</option>
            </Select>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleAssign} loading={saving}>Assign Trainer</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.03em', display: 'block', marginBottom: 6 };
